import cameras from "../data/cameras";
import cameraTypes from "../data/camera-types.json";
import scenarios from "../data/scenarios.json";
import lightingConditions from "../data/lighting.json";
import parameters from "../data/parameters.json";
import recipes from "../data/recipes-curated.json";
import parameterPages from "../data/parameter-pages.json";
import { isPublished } from "./publishing";

/**
 * A search index built at module load from data already in the bundle.
 *
 * No search service, no build step, no dependency — the whole corpus is
 * about 600 short records and the datasets are already shipped for other
 * reasons, so the index costs a few milliseconds of startup and nothing
 * else. A hosted search product would be a monthly bill and a network
 * round trip to search text the browser is already holding.
 *
 * Scheduled pages are excluded, so search can't surface something the rest
 * of the site hasn't revealed yet.
 */

function normalize(text) {
  return String(text || "")
    .toLowerCase()
    // Fold punctuation so "zv-e10" matches "zve10" and "a7 iv" matches "a7iv".
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function buildIndex() {
  const now = new Date();
  const entries = [];

  for (const camera of cameras) {
    const full = camera.capabilityLevel === "full";
    entries.push({
      kind: "camera",
      title: camera.name,
      // Exposure-only cameras have no hub page of their own, so they point
      // at the builder prefilled rather than at a page that doesn't exist.
      path: full ? `/cameras/${camera.id}` : `/builder?camera=${camera.id}`,
      subtitle: full ? `${camera.manufacturer} · settings guide` : `${camera.manufacturer} · open in the builder`,
      terms: normalize([camera.name, camera.manufacturer, ...(camera.aliases || [])].join(" ")),
      weight: full ? 3 : 2,
    });
  }

  // One entry per camera type with at least one live recipe — so a
  // search for "drone settings" or "action camera settings" lands on the
  // type gallery rather than only ever matching an individual model.
  for (const t of cameraTypes) {
    const hasFullSpec = cameras.some((c) => c.type === t.id && c.capabilityLevel === "full");
    if (!hasFullSpec) continue;
    entries.push({
      kind: "type",
      title: `${t.name} settings`,
      path: `/gallery/${t.id}`,
      subtitle: t.description,
      terms: normalize([t.name, t.singular, t.id.replace(/-/g, " ")].join(" ")),
      weight: 2,
    });
  }

  for (const p of parameterPages) {
    if (!isPublished(p, now)) continue;
    const camera = cameras.find((c) => c.id === p.cameraId);
    const parameter = parameters.find((x) => x.id === p.parameterId);
    if (!camera || !parameter) continue;
    entries.push({
      kind: "reference",
      title: `${camera.name} ${parameter.name.toLowerCase()}`,
      path: `/cameras/${camera.id}/${parameter.id}`,
      subtitle: parameter.summary,
      terms: normalize([camera.name, ...(camera.aliases || []), parameter.name, parameter.queryShape.replace("{camera}", camera.name)].join(" ")),
      weight: 2,
    });
  }

  for (const r of recipes) {
    if (!isPublished(r, now)) continue;
    const camera = cameras.find((c) => c.id === r.cameraId);
    const scenario = scenarios.find((s) => s.id === r.scenarioId);
    const lighting = lightingConditions.find((l) => l.id === r.lightingId);
    if (!camera || !scenario || !lighting) continue;

    // A merged page carries several situations; index all of them so the
    // absorbed queries still find their way to the page that answers them.
    const covered = (r.covers || [])
      .map((c) => {
        const sc = scenarios.find((x) => x.id === c.scenarioId);
        const li = lightingConditions.find((x) => x.id === c.lightingId);
        return sc && li ? `${sc.name} ${li.name}` : "";
      })
      .join(" ");

    entries.push({
      kind: "recipe",
      title: `${camera.name}: ${scenario.name}`,
      path: `/camera/${r.slug}`,
      subtitle: `in ${lighting.name}`,
      terms: normalize([camera.name, ...(camera.aliases || []), scenario.name, scenario.category, lighting.name, covered].join(" ")),
      weight: 1,
    });
  }

  return entries;
}

let cached = null;
function index() {
  if (!cached) cached = buildIndex();
  return cached;
}

/**
 * Scores on whole-token matches rather than a bare substring test, so
 * "x4" doesn't match every camera containing "x" and "iso" doesn't match
 * "isolation". A term matching the start of a token scores lower than an
 * exact token but higher than nothing, which is what makes typing feel
 * responsive before you've finished a word.
 */
export function search(query, limit = 25) {
  const q = normalize(query);
  if (q.length < 2) return [];
  const queryTokens = q.split(" ").filter(Boolean);

  const results = [];
  for (const entry of index()) {
    const tokens = entry.terms.split(" ");
    let score = 0;
    let matchedAll = true;

    for (const qt of queryTokens) {
      const exact = tokens.includes(qt);
      const prefix = !exact && tokens.some((t) => t.startsWith(qt));
      if (exact) score += 10;
      else if (prefix) score += 4;
      else {
        matchedAll = false;
        break;
      }
    }

    // Every query token has to land somewhere. Partial matches turn a
    // search for "pocket 3 shutter" into a list of everything containing
    // "3", which is worse than no results.
    if (!matchedAll) continue;

    score += entry.weight;
    if (normalize(entry.title).startsWith(q)) score += 6;
    results.push({ ...entry, score });
  }

  return results.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title)).slice(0, limit);
}

export function indexSize() {
  return index().length;
}
